import assert from 'shared/assert'

const sort_by_string_property = <
	P extends string,
	T extends { [key in P]: string }
>(array: T[], property: P) => array.sort((a, b) => a[property].localeCompare(b[property]))

const sort_by_number_property = <
	P extends string,
	T extends { [key in P]: number }
>(array: T[], property: P) => array.sort((a, b) => a[property] - b[property])

export const sort_pistol_makes = (pistol_makes: TrexPistolMake[]) => sort_by_number_property(pistol_makes, `storefront_sort_order`)

export function get_families<
	PISTOL_MAKE_REF extends TrexPistolMake[`pistol_make_ref`],
	PISTOL_FAMILY extends Pick<TrexPistolFamily, `storefront_sort_order`>
>(params: {
	pistol_make: { pistol_make_ref: PISTOL_MAKE_REF }
	pistol_make_ref_to_pistol_families_with_availability: { [key in PISTOL_MAKE_REF]: PISTOL_FAMILY[] }
}): PISTOL_FAMILY[]
export function get_families<
	PISTOL_MAKE_REF extends TrexPistolMake[`pistol_make_ref`],
	PISTOL_FAMILY extends Pick<TrexPistolFamily, `storefront_sort_order`>
>(params: {
	pistol_make: { pistol_make_ref: PISTOL_MAKE_REF } | null
	pistol_make_ref_to_pistol_families_with_availability: { [key in PISTOL_MAKE_REF]: PISTOL_FAMILY[] }
}): PISTOL_FAMILY[] | null
export function get_families<
	PISTOL_MAKE_REF extends TrexPistolMake[`pistol_make_ref`],
	PISTOL_FAMILY extends Pick<TrexPistolFamily, `storefront_sort_order`>
>({
	pistol_make,
	pistol_make_ref_to_pistol_families_with_availability,
}: {
	pistol_make: { pistol_make_ref: PISTOL_MAKE_REF } | null
	pistol_make_ref_to_pistol_families_with_availability: { [key in PISTOL_MAKE_REF]: PISTOL_FAMILY[] }
}) {
	return pistol_make && sort_by_number_property(pistol_make_ref_to_pistol_families_with_availability[pistol_make.pistol_make_ref], `storefront_sort_order`)
}

export function get_cartridges<
	PISTOL_FAMILY_REF extends TrexPistolFamily[`pistol_family_ref`],
	CARTRIDGE extends Pick<TrexCartridge, `sort_order`>
>(params: {
	pistol_family: { pistol_family_ref: PISTOL_FAMILY_REF }
	pistol_family_ref_to_cartridges_to_pistols: { [key in PISTOL_FAMILY_REF]: CARTRIDGE[] }
}): CARTRIDGE[]
export function get_cartridges<
	PISTOL_FAMILY_REF extends TrexPistolFamily[`pistol_family_ref`],
	CARTRIDGE extends Pick<TrexCartridge, `sort_order`>
>(params: {
	pistol_family: { pistol_family_ref: PISTOL_FAMILY_REF } | null
	pistol_family_ref_to_cartridges_to_pistols: { [key in PISTOL_FAMILY_REF]: CARTRIDGE[] }
}): CARTRIDGE[] | null
export function get_cartridges<
	PISTOL_FAMILY_REF extends TrexPistolFamily[`pistol_family_ref`],
	CARTRIDGE extends Pick<TrexCartridge, `sort_order`>
>({
	pistol_family,
	pistol_family_ref_to_cartridges_to_pistols,
}: {
	pistol_family: { pistol_family_ref: PISTOL_FAMILY_REF } | null
	pistol_family_ref_to_cartridges_to_pistols: { [key in PISTOL_FAMILY_REF]: CARTRIDGE[] }
}) {
	return pistol_family && sort_by_number_property(pistol_family_ref_to_cartridges_to_pistols[pistol_family.pistol_family_ref], `sort_order`)
}

export function get_pistol_models<
	PISTOL_MODEL extends Pick<TrexPistolModel, `storefront_sort_order`>
>(cartridge: { pistol_models: PISTOL_MODEL[] }): PISTOL_MODEL[]
export function get_pistol_models<
	PISTOL_MODEL extends Pick<TrexPistolModel, `storefront_sort_order`>
>(cartridge: { pistol_models: PISTOL_MODEL[] } | null): PISTOL_MODEL[] | null
export function get_pistol_models<
	PISTOL_MODEL extends Pick<TrexPistolModel, `storefront_sort_order`>
>(cartridge: { pistol_models: PISTOL_MODEL[] } | null) {
	return cartridge && sort_by_number_property(cartridge.pistol_models, `storefront_sort_order`)
}

export function get_pistol_lights<
	PISTOL_REF extends TrexPistol[`pistol_ref`],
	PISTOL_LIGHT extends Pick<TrexPistolLight, `name`>
>({ pistol, pistol_ref_to_lights_to_holster_availabilities }: {
	pistol: { pistol_ref: PISTOL_REF }
	pistol_ref_to_lights_to_holster_availabilities: { [key in PISTOL_REF]: PISTOL_LIGHT[] }
}): PISTOL_LIGHT[]
export function get_pistol_lights<
	PISTOL_REF extends TrexPistol[`pistol_ref`],
	PISTOL_LIGHT extends Pick<TrexPistolLight, `name`>
>({ pistol, pistol_ref_to_lights_to_holster_availabilities }: {
	pistol: { pistol_ref: PISTOL_REF } | null
	pistol_ref_to_lights_to_holster_availabilities: { [key in PISTOL_REF]: PISTOL_LIGHT[] }
}): PISTOL_LIGHT[] | null
export function get_pistol_lights<
	PISTOL_REF extends TrexPistol[`pistol_ref`],
	PISTOL_LIGHT extends Pick<TrexPistolLight, `name`>
>({ pistol, pistol_ref_to_lights_to_holster_availabilities }: {
	pistol: { pistol_ref: PISTOL_REF } | null
	pistol_ref_to_lights_to_holster_availabilities: { [key in PISTOL_REF]: PISTOL_LIGHT[] }
}) {
	return pistol && sort_by_string_property(pistol_ref_to_lights_to_holster_availabilities[pistol.pistol_ref], `name`)
}

const get_default_make = <
	PISTOL_MAKE extends Pick<TrexPistolMake, `storefront_sort_order`>
>(pistol_makes: PISTOL_MAKE[]) => sort_by_number_property(pistol_makes, `storefront_sort_order`)[0]

const get_default_family = <
	PISTOL_FAMILY extends Pick<TrexHolsterAvailability, `available_now`>
>(pistol_families: PISTOL_FAMILY[]) => pistol_families.find(({ available_now }) => available_now) || pistol_families[0]

const get_default_model = <
	PISTOL_MODEL extends Pick<TrexHolsterAvailability, `available_now`>
>(pistol_models: PISTOL_MODEL[]) => pistol_models.find(({ available_now }) => available_now) || pistol_models[0]

const get_default_holster_availability = <
	HOLSTER_AVAILABILITY extends Pick<TrexHolsterAvailability, `left_hand` | `threaded_barrel`>
>(holster_availabilites: HOLSTER_AVAILABILITY[]) => {
	const holster_availability = holster_availabilites
		.slice()
		.sort((a, b) => (a.left_hand === b.left_hand) ? 0 : a.left_hand ? 1 : -1) // false first
		.sort((a, b) => (a.threaded_barrel === b.threaded_barrel) ? 0 : a.threaded_barrel ? 1 : -1) // false first
		.at(0)
	assert(holster_availability)
	return holster_availability
}

export const get_initial_values = <
	PISTOL_MAKE_REF extends TrexPistolMake[`pistol_make_ref`],
	PISTOL_MAKE extends { pistol_make_ref: PISTOL_MAKE_REF } & Pick<TrexPistolMake, `storefront_sort_order`>,
	PISTOL_FAMILY_REF extends TrexPistolFamily[`pistol_family_ref`],
	PISTOL_FAMILY extends { pistol_family_ref: PISTOL_FAMILY_REF } & Pick<TrexPistolFamily, `storefront_sort_order`>,
	CARTRIDGE extends Pick<TrexCartridge, `cartridge_ref` | `sort_order`> & { pistol_models: PISTOL_MODEL[] },
	PISTOL_MODEL extends Pick<TrexPistolModel, `pistol_model_ref` | `storefront_sort_order`> & { pistol: PISTOL },
	PISTOL extends Pick<TrexPistol, `pistol_ref`>,
	PISTOL_LIGHT extends Pick<TrexPistolLight, `pistol_light_ref` | `name`> & { holster_availabilities: HOLSTER_AVAILABILITY[] },
	HOLSTER_AVAILABILITY extends Pick<TrexHolsterAvailability, `left_hand` | `threaded_barrel`>
>({
		pistol_makes,
		pistol_make_ref_to_pistol_families_with_availability,
		pistol_family_ref_to_cartridges_to_pistols,
		pistol_ref_to_lights_to_holster_availabilities,
		pistol_make_ref,
		pistol_family_ref,
		cartridge_ref,
		pistol_model_ref,
		pistol_light_ref,
		left_hand,
		threaded_barrel,
	}: {
	pistol_makes: PISTOL_MAKE[]
	pistol_make_ref_to_pistol_families_with_availability: { [key in PISTOL_MAKE_REF]: PISTOL_FAMILY[] }
	pistol_family_ref_to_cartridges_to_pistols: { [key in PISTOL_FAMILY_REF]: CARTRIDGE[] }
	pistol_ref_to_lights_to_holster_availabilities: { [key in number]: PISTOL_LIGHT[] }
	pistol_make_ref?: number
	pistol_family_ref?: number
	cartridge_ref?: number
	pistol_model_ref?: number
	pistol_light_ref?: number
	left_hand?: boolean
	threaded_barrel?: boolean
}) => {
	const pistol_make = pistol_makes.find(
		pistol_make => pistol_make.pistol_make_ref === pistol_make_ref
	) || null

	const families = get_families({ pistol_make, pistol_make_ref_to_pistol_families_with_availability })
	const pistol_family = families && families.find(
		pistol_family => pistol_family.pistol_family_ref === pistol_family_ref
	) || null

	const cartridges = get_cartridges({ pistol_family, pistol_family_ref_to_cartridges_to_pistols })
	const cartridge = cartridges && cartridges.find(
		cartridge => cartridge.cartridge_ref === cartridge_ref
	) || null

	const models = get_pistol_models(cartridge)
	const pistol_model = models && models.find(
		pistol_model => pistol_model.pistol_model_ref === pistol_model_ref
	) || null

	const lights = get_pistol_lights({ pistol: pistol_model?.pistol || null, pistol_ref_to_lights_to_holster_availabilities })
	const pistol_light = lights && lights.find(
		light => light.pistol_light_ref === pistol_light_ref
	) || null

	const holster_availabilities = pistol_light?.holster_availabilities
	const holster_availability = holster_availabilities && (
		holster_availabilities.find(holster_availability => left_hand === holster_availability.left_hand
			&& threaded_barrel === holster_availability.threaded_barrel)
		|| get_default_holster_availability(holster_availabilities)
	)

	return {
		pistol_make,
		pistol_family,
		cartridge,
		pistol_model,
		pistol_light,
		left_hand: !!holster_availability?.left_hand,
		threaded_barrel: !!holster_availability?.threaded_barrel,
	}
}

export const get_default_values = <
	PISTOL_MAKE_REF extends TrexPistolMake[`pistol_make_ref`],
	PISTOL_MAKE extends { pistol_make_ref: PISTOL_MAKE_REF } & Pick<TrexPistolMake, `storefront_sort_order`>,
	PISTOL_FAMILY_REF extends TrexPistolFamily[`pistol_family_ref`],
	PISTOL_FAMILY extends { pistol_family_ref: PISTOL_FAMILY_REF } & Pick<TrexPistolFamily, `storefront_sort_order`> & Pick<TrexHolsterAvailability, `available_now`>,
	CARTRIDGE extends Pick<TrexCartridge, `cartridge_ref` | `sort_order`> & { pistol_models: PISTOL_MODEL[] },
	PISTOL_MODEL extends Pick<TrexPistolModel, `pistol_model_ref` | `storefront_sort_order`>
		& Pick<TrexHolsterAvailability, `available_now`> & { pistol: PISTOL },
	PISTOL extends Pick<TrexPistol, `pistol_ref`>,
	PISTOL_LIGHT extends Pick<TrexPistolLight, `pistol_light_ref` | `name`> & { holster_availabilities: HOLSTER_AVAILABILITY[] },
	HOLSTER_AVAILABILITY extends Pick<TrexHolsterAvailability, `left_hand` | `threaded_barrel`>
>({
		pistol_makes,
		pistol_make_ref_to_pistol_families_with_availability,
		pistol_family_ref_to_cartridges_to_pistols,
		pistol_ref_to_lights_to_holster_availabilities,
	}: {
	pistol_makes: PISTOL_MAKE[]
	pistol_make_ref_to_pistol_families_with_availability: { [key in PISTOL_MAKE_REF]: PISTOL_FAMILY[] }
	pistol_family_ref_to_cartridges_to_pistols: { [key in PISTOL_FAMILY_REF]: CARTRIDGE[] }
	pistol_ref_to_lights_to_holster_availabilities: { [key in number]: PISTOL_LIGHT[] }
}) => {
	const pistol_make = get_default_make(pistol_makes)
	const pistol_family = get_default_family(get_families({ pistol_make, pistol_make_ref_to_pistol_families_with_availability }))
	const cartridge = get_cartridges({ pistol_family, pistol_family_ref_to_cartridges_to_pistols })[0]
	const pistol_model = get_default_model(get_pistol_models(cartridge))
	const pistol_light = get_pistol_lights({ pistol: pistol_model.pistol, pistol_ref_to_lights_to_holster_availabilities })[0]
	const holster_availabilities = pistol_light.holster_availabilities
	const holster_availability = get_default_holster_availability(holster_availabilities)

	return {
		pistol_make,
		pistol_family,
		cartridge,
		pistol_model,
		pistol_light,
		left_hand: holster_availability.left_hand,
		threaded_barrel: holster_availability.threaded_barrel,
	}
}
export const availability_description = ({
	available_year,
	available_quarter,
}: Pick<TrexHolsterAvailability, `available_year` | `available_quarter`>) => available_year && available_quarter
	? `(Planned Q${ available_quarter } ${ available_year })`
	: `(Planned some day)`
